import { createIdentifier } from '@blocksuite/global/di';
import type { DeepPartial } from '@blocksuite/global/utils';
import type { ExtensionType } from '@blocksuite/store';
import type { Signal } from '@preact/signals-core';
import { z } from 'zod';

import { NodePropsSchema } from '../utils/index.js';

/** Focus margins offered in the settings, in viewports; the smallest is the
 * minimum. */
export const EDGELESS_FOCUS_MARGIN_VALUES = [0.25, 0.5, 1, 2, 4] as const;
export const EDGELESS_FOCUS_MARGIN_DEFAULT = 1;

export const GeneralSettingSchema = z
  .object({
    edgelessScrollZoom: z.boolean().default(true),
    /** @deprecated Compatibility only; the Select/Pan toolbar owns drag routing. */
    edgelessCanvasDragPan: z.boolean().default(false),
    /** @deprecated Compatibility only; use edgelessScrollZoom. */
    edgelessMouseWheelZoom: z.boolean().default(false),
    edgelessDisableScheduleUpdate: z.boolean().default(false),
    /** Canvas blocks render only in the focus field; off renders them all. */
    edgelessFocusField: z.boolean().default(true),
    /** Viewports around the visible area in which canvas blocks render (the
     * focus field); further out they render when the viewport nears. */
    edgelessFocusMargin: z
      .number()
      .min(EDGELESS_FOCUS_MARGIN_VALUES[0])
      .max(
        EDGELESS_FOCUS_MARGIN_VALUES[EDGELESS_FOCUS_MARGIN_VALUES.length - 1]
      )
      .default(EDGELESS_FOCUS_MARGIN_DEFAULT),
    docCanvasPreferView: z
      .enum(['affine:embed-linked-doc', 'affine:embed-synced-doc'])
      .default('affine:embed-synced-doc'),
  })
  .merge(NodePropsSchema);

export type EditorSetting = z.infer<typeof GeneralSettingSchema>;

export interface EditorSettingService {
  setting$: Signal<DeepPartial<EditorSetting>>;
  set?: (
    key: keyof EditorSetting,
    value: EditorSetting[keyof EditorSetting]
  ) => void;
}

export const EditorSettingProvider = createIdentifier<EditorSettingService>(
  'AffineEditorSettingProvider'
);

export function EditorSettingExtension(
  service: EditorSettingService
): ExtensionType {
  return {
    setup: di => {
      di.override(EditorSettingProvider, () => service);
    },
  };
}
